import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, CheckSquare, AlertCircle, Phone, User } from "lucide-react";
import { followUpPlanningService } from "@/services/followUpPlanning";

interface FollowUpPlanningProps {
  aiScore: number;
  category: string;
  symptoms: string[];
  urgency: 'routine' | 'scheduled' | 'prompt' | 'urgent';
}

const FollowUpPlanning = ({ aiScore, category, symptoms, urgency }: FollowUpPlanningProps) => {
  const [followUpPlan, setFollowUpPlan] = React.useState<any>(null);
  const [trackingQuestions, setTrackingQuestions] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadPlan = async () => {
      try {
        const [plan, questions] = await Promise.all([
          followUpPlanningService.generateFollowUpPlan(aiScore, category, symptoms, urgency),
          Promise.resolve(followUpPlanningService.getSymptomTrackingQuestions(category, symptoms))
        ]);
        setFollowUpPlan(plan);
        setTrackingQuestions(questions || []);
      } catch (error) {
        console.error('Error loading follow-up plan:', error);
        // Set fallback data
        setFollowUpPlan({
          timeline: 'Monitor symptoms for changes',
          checkPoints: [{
            timeFrame: '24-48 hours',
            description: 'Initial monitoring period',
            action: 'self_monitor',
            questions: ['Are symptoms improving?', 'Any new concerns?'],
            intelligentPrompts: ['Track symptom changes'],
            symptomSpecific: false
          }],
          monitoringFocus: ['Overall symptom progression', 'Daily function impact'],
          escalationTriggers: ['Symptoms worsen', 'New symptoms develop']
        });
        setTrackingQuestions(['Rate your symptom severity 1-10', 'How are symptoms affecting daily activities?']);
      } finally {
        setLoading(false);
      }
    };
    loadPlan();
  }, [aiScore, category, symptoms, urgency]);

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-1/3 mb-3"></div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  // Additional safety checks
  if (!followUpPlan) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Unable to load follow-up plan. Please try refreshing the page.
      </div>
    );
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'self_monitor': return <CheckSquare className="h-4 w-4" />;
      case 'contact_provider': return <Phone className="h-4 w-4" />;
      case 'seek_urgent_care': return <AlertCircle className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'self_monitor': return 'text-blue-600';
      case 'contact_provider': return 'text-orange-600';
      case 'seek_urgent_care': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getActionBgColor = (action: string) => {
    switch (action) {
      case 'self_monitor': return 'bg-blue-50 border-blue-200';
      case 'contact_provider': return 'bg-orange-50 border-orange-200';
      case 'seek_urgent_care': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-4">
      {/* Timeline Overview */}
      <Card className="p-4 border-primary/20 bg-primary/5">
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-primary">Follow-Up Timeline</h3>
          </div>
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-primary/70" />
            <span className="text-sm font-medium text-primary/80">Duration:</span>
            <Badge variant="outline" className="text-primary border-primary/30">
              {followUpPlan.timeline}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Follow-Up Checkpoints */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-foreground mb-3">Follow-Up Schedule:</h4>
        {followUpPlan.checkPoints?.map((checkpoint: any, index: number) => (
          <Card key={index} className={`p-4 ${getActionBgColor(checkpoint.action)}`}>
            <div className="space-y-3">
              {/* Checkpoint Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`${getActionColor(checkpoint.action)}`}>
                    {getActionIcon(checkpoint.action)}
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {checkpoint.timeFrame}
                  </span>
                </div>
                <Badge 
                  variant="outline" 
                  className={`text-xs ${getActionColor(checkpoint.action)} border-current`}
                >
                  {checkpoint.action.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                </Badge>
              </div>

              {/* Checkpoint Description */}
              <p className="text-sm text-foreground/80">
                {checkpoint.description}
              </p>

              {/* Checkpoint Questions */}
              <div className="space-y-2">
                <span className="text-xs font-medium text-foreground/70">Self-Assessment Questions:</span>
                {checkpoint.questions?.map((question: string, qIndex: number) => (
                  <div key={qIndex} className="flex items-start space-x-2">
                    <div className="text-foreground/50 text-xs mt-1">•</div>
                    <p className="text-xs text-foreground/70">
                      {question}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>

    </div>
  );
};

export default FollowUpPlanning;